import { useTranslation } from 'react-i18next';

import React, { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';

import { AuthVariant } from './AuthBox';
import Button from '../../01_atoms/Button/Button';
import PasswordInput from '../../01_atoms/PasswordInput/PasswordInput';
import TextInput from '../../01_atoms/TextInput/TextInput';
import {
  getAuthErrorMessage,
  waitForRequestConfirmation,
} from '../../../utils/authError';

type ConfirmResetPasswordProps = {
  code: string;
  email: string;
  errorInformation: null | {
    position: string;
    element: React.ReactNode;
  };
  setCode: (v: string) => void;
  setCurrentVariant: (variant: AuthVariant) => void;
  setPassword: (v: string) => void;
};

export default function ConfirmResetPassword({
  code,
  email,
  errorInformation,
  setCode,
  setCurrentVariant,
  setPassword,
}: ConfirmResetPasswordProps) {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(false);
  const [sendMessage, setSendMessage] = useState('');
  const [sendError, setSendError] = useState('');
  const sendingRef = useRef(false);

  useEffect(() => {
    if (code || typeof window === 'undefined') return;
    const urlCode = new URLSearchParams(window.location.search).get(
      'resetCode',
    );
    if (urlCode) setCode(urlCode);
  }, [code, setCode]);

  const resendCode = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setIsSending(true);
    setSendMessage('');
    setSendError('');
    try {
      const status = await waitForRequestConfirmation(
        Auth.forgotPassword(email.trim().toLowerCase().replace('@', '_at_'), {
          resetTarget: 'app',
        }),
      );
      setSendMessage(
        status === 'confirmed'
          ? t('EMAIL_SENT')
          : t('EMAIL_DELIVERY_UNCONFIRMED'),
      );
    } catch (error) {
      setSendError(getAuthErrorMessage(t, error));
    } finally {
      sendingRef.current = false;
      setIsSending(false);
    }
  };

  return (
    <>
      <React.Fragment key="confirm-submit-fragment">
        {code ? (
          <p role="status" style={{ marginTop: '3.5rem' }}>
            Kod resetu został bezpiecznie wczytany z linku.
          </p>
        ) : (
          <TextInput
            id="code"
            type="text"
            name="code"
            onChange={(e): void => {
              setCode(e.target.value);
            }}
            required
            placeholder={t('CODE_FROM_EMAIL')}
            error={
              errorInformation?.position === 'code' && errorInformation.element
            }
            style={{ marginTop: '3.5rem' }}
          />
        )}

        <PasswordInput
          id="password"
          name="password"
          onChange={setPassword}
          placeholder={t('PASSWORD')}
          showValidation={true}
          required
          error={
            errorInformation?.position === 'password' &&
            errorInformation.element
          }
        />

        <Button
          type="submit"
          disabled={isSending}
          style={{
            cursor: 'pointer',
            display: 'block',
            marginTop: '3rem',
          }}
        >
          {t('BEST_TIGEN')}
        </Button>

        <Button
          type="button"
          disabled={isSending || !email}
          onClick={resendCode}
          style={{ marginTop: '1rem' }}
        >
          {t('SEND_CODE_AGAIN')}
        </Button>
        {sendError ? (
          <p role="alert" style={{ color: '#d33' }}>
            {sendError}
          </p>
        ) : null}
        {sendMessage ? <p role="status">{sendMessage}</p> : null}

        <Button
          type="button"
          variant="text"
          style={{ marginTop: '2rem', color: 'black' }}
          onClick={(): void => setCurrentVariant('signin')}
        >
          {t('ZUR_CK_ZUM_LOGIN')}
        </Button>
      </React.Fragment>
    </>
  );
}
